import React, { createContext, useContext, useEffect } from 'react'
import { User } from '@supabase/supabase-js'
import { useAuthStore } from '@/store/authStore'
import { useCartStore } from '@/store/cartStore'
import { getCurrentUser, getUserProfile, onAuthStateChange } from '@/lib/auth'

interface AuthContextType {
  user: User | null
  profile: any
  isLoading: boolean
  isAuthenticated: boolean
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const { user, profile, isLoading, isAuthenticated, setUser, setProfile, setLoading } = useAuthStore()
  const { setUserId, loadUserCart, clearCart } = useCartStore()

  useEffect(() => {
    // İlk yüklemede mevcut kullanıcıyı kontrol et
    const initializeAuth = async () => {
      try {
        setLoading(true)
        const currentUser = await getCurrentUser()
        setUser(currentUser)
        
        if (currentUser) {
          const userProfile = await getUserProfile(currentUser.id)
          setProfile(userProfile)
          
          // Kullanıcı zaten giriş yapmışsa sepetini yükle
          setUserId(currentUser.id)
          loadUserCart(currentUser.id)
        } else {
          // Giriş yapmamış kullanıcı için misafir modu
          setUserId(null)
        }
      } catch (error) {
        console.error('Auth initialization error:', error)
      } finally {
        setLoading(false)
      }
    }

    initializeAuth()

    // Auth state değişikliklerini dinle
    const { data: { subscription } } = onAuthStateChange(async (user) => {
      setUser(user)
      
      if (user) {
        const userProfile = await getUserProfile(user.id)
        setProfile(userProfile)
        
        // Kullanıcı giriş yaptığında sepetini yükle
        setUserId(user.id)
        loadUserCart(user.id)
      } else {
        setProfile(null)
        
        // Kullanıcı çıkış yaptığında sepeti temizle ve misafir moduna geç
        setUserId(null)
        clearCart()
      }
    })

    return () => {
      subscription.unsubscribe()
    }
  }, [setUser, setProfile, setLoading])

  const value = {
    user,
    profile,
    isLoading,
    isAuthenticated,
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}